/**
 * Lean Amplitude helpers for in-space journeys.
 * Always go through window.baglessTrack (consent-gated in the layout).
 */

export const AMPLITUDE_EVENTS = {
  ENTERED_ROOM: 'User entered the room',
  MOVED_CAMERA: 'User moved camera',
  CAMERA_OVER_OBJECT: 'Camera over object',
  CAMERA_LEFT_OBJECT: 'Camera left object',
  STARTED_VAPI: 'User started VAPI',
  VAPI_CONNECTED: 'User VAPI connected',
  ENDED_VAPI: 'User ended VAPI',
  SENT_VAPI_COMMAND: 'User sent VAPI command',
  VAPI_CALLED_TOOL: 'VAPI called a tool',
  VAPI_ANSWERED_USER: 'VAPI answered user',
  TALKED_WITH_VAPI: 'User talked with VAPI',
  CHANGED_SCENE: 'User changed scene',
  TOOK_SEAT: 'User took a seat',
  LEFT_SEAT: 'User left a seat',
  SAW_INFO_PANEL: 'User Opened Info Element Modal',
  INTRO_STARTED: 'Intro animation started',
  INTRO_ENDED: 'Intro animation ended',
  SESSION_OVER: 'Session over',
  OPENED_CHAT: 'User opened chat',
  CLOSED_CHAT: 'User closed chat',
  OPENED_PEOPLE: 'User opened people',
  CLOSED_PEOPLE: 'User closed people',
  OPENED_CONVERSATION: 'User opened conversation',
  CLOSED_CONVERSATION: 'User closed conversation',
  SENT_CHAT_MESSAGE: 'User sent chat message',
  CLICKED_UI: 'User clicked UI',
  OPENED_INPUT_FIELD: 'User opened input field',
  SUBMITTED_INPUT_FIELD: 'User submitted input field',
  INPUT_FIELD_VALIDATION_FAILED: 'Input field validation failed',
  FUNNEL_STEP: 'Funnel step',
  VAPI_STORED_SESSION_DATA: 'VAPI stored session data',
  DECLINED_CONSENT_DIALOGUE: 'User declined consent dialogue',
};

const CAMERA_MOVE_COOLDOWN_MS = 8000;
const CAMERA_HOVER_DELAY_MS = 0;

export function getAnalyticsProps(model, extra = {}) {
  const config = model?.config || {};
  const scene = model?.view?.currentScene || model?.currentScene || {};
  const props = {
    space_id: config.worldId ?? null,
    space_name: config.worldName || null,
    scene_id: scene.id || config.sceneId || null,
    scene_name: scene.publicName || scene.name || config.sceneName || null,
    is_guest: !!config.isGuest,
  };
  Object.keys(extra).forEach((key) => {
    const value = extra[key];
    if (value !== undefined && value !== null && value !== '') {
      props[key] = value;
    }
  });
  return props;
}

export function trackAnalytics(model, eventName, extra = {}) {
  if (typeof window === 'undefined' || typeof window.baglessTrack !== 'function') return;
  const config = model?.config;
  if (!config || !eventName) return;
  window.baglessTrack(config, eventName, getAnalyticsProps(model, extra));
}

/** Flush queued Amplitude events before a redirect or unload. */
export function flushAnalytics() {
  try {
    const result = typeof window !== 'undefined' && window.amplitude?.flush?.();
    return result && typeof result.then === 'function' ? result.catch(() => {}) : Promise.resolve();
  } catch (e) {
    return Promise.resolve();
  }
}

const OBJECT_TYPE_LABELS = {
  infoElement: 'Info element',
  webLink: 'Link',
  sceneChange: 'Scene change',
  guestSeat: 'Chair',
  vraiAgent: 'VAPI agent',
  vapiButton: 'VAPI button',
  vapiUserCamera: 'VAPI camera',
  vapiTranscription: 'VAPI transcription',
  screenStream: 'Screen share',
  tourScreenShare: 'Tour screen share',
  profile: 'Profile',
  imageGallery: 'Image gallery',
  videoGallery: 'Video gallery',
  videoPlayer: 'Video player',
  videoPlayerWebGL: 'Video player',
  youtube: 'YouTube',
  vrMusicObject: 'Music',
  iframeElement: 'iFrame',
  iframeAgent: 'iFrame',
  embeddedWebpage: 'Embedded webpage',
  timerElement: 'Timer',
  baglessRoom: 'Bagless room',
  chessGame: 'Chess',
  payPalDonation: 'PayPal donation',
  shopLink: 'Shop',
  inputFieldGuess: 'Input guess',
  inputField: 'Input field',
  vrInvisibleAction: 'Invisible action',
};

function clip(value, max = 80) {
  if (value == null || value === '') return null;
  const text = String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  return text.length > max ? text.slice(0, max) : text;
}

function findSceneObjectRecord(view, sourceId) {
  const idStr = String(sourceId);
  const scenes = view?.model?.scenes;
  if (!scenes) return null;
  const lists = Array.isArray(scenes)
    ? scenes.map((scene) => scene?.objects)
    : Object.values(scenes).map((scene) => scene?.objects);
  for (const objects of lists) {
    if (!Array.isArray(objects)) continue;
    const found = objects.find((obj) => obj && String(obj.id) === idStr);
    if (found) return found;
  }
  return null;
}

function resolveSceneName(view, sceneId) {
  if (sceneId == null) return null;
  const idStr = String(sceneId);
  const scenes = view?.model?.scenes;
  if (!scenes) return null;
  if (!Array.isArray(scenes) && scenes[sceneId]) {
    return scenes[sceneId].publicName || scenes[sceneId].name || null;
  }
  const list = Array.isArray(scenes) ? scenes : Object.values(scenes);
  const scene = list.find((item) => item && String(item.id) === idStr);
  return scene?.publicName || scene?.name || null;
}

function findChair(view, sourceId) {
  const idStr = String(sourceId);
  return view?.vraiAgentChairs?.find((c) => (
    [c?._getAgentId?.(), c?.dbConfig?.id, c?.id].some((value) => value != null && String(value) === idStr)
  )) || null;
}

function instanceNameFromConfig(cfg, view, chair) {
  if (!cfg && !chair) return null;
  return clip(
    chair?._getAgentDisplayName?.()
    || cfg?.chairName
    || cfg?.data?.title
    || cfg?.title
    || cfg?.publicName
    || cfg?.url
    || resolveSceneName(view, cfg?.goToSceneId || cfg?.targetRoomId)
    || cfg?.name
  );
}

function resolveElementType({ mesh, parent, cfg, isVapi }) {
  if (isVapi) return 'vraiAgent';
  const skip = new Set(['Mesh', 'Group', 'Object3D', 'lockable', 'delete', 'settings', 'menuPosition']);
  const candidates = [cfg?.type, parent?.type, parent?.dbConfig?.type, mesh?.type];
  for (const raw of candidates) {
    if (!raw || typeof raw !== 'string') continue;
    const normalized = raw.replace(/Dummy$/i, '');
    if (skip.has(raw) || skip.has(normalized)) continue;
    if (OBJECT_TYPE_LABELS[normalized]) return normalized;
    if (OBJECT_TYPE_LABELS[raw]) return raw;
    if (normalized && normalized !== 'unknown') return normalized;
  }
  return 'unknown';
}

export function getObjectAnalyticsMeta(view, sourceId, hitObject) {
  if (sourceId == null) return null;
  const idStr = String(sourceId);
  if (!view) {
    return {
      object_id: idStr,
      element_type: 'unknown',
      object_type: 'unknown',
      object_label: 'Unknown',
      is_vapi: false,
    };
  }

  const mesh = hitObject || view.find3dObject?.(sourceId);
  const parent = mesh?.parent;
  const chair = findChair(view, sourceId);
  const sceneObj = findSceneObjectRecord(view, sourceId);
  const cfg = chair?.dbConfig || parent?.dbConfig || sceneObj || null;
  const isVapi = !!(chair || cfg?.type === 'vraiAgent' || mesh?.type === 'vraiAgent' || parent?.type === 'vraiAgent');
  const type = resolveElementType({ mesh, parent, cfg, isVapi });
  const label = OBJECT_TYPE_LABELS[type] || type;

  const name = instanceNameFromConfig(cfg, view, chair);
  const targetSceneId = cfg?.goToSceneId || parent?.targetRoomId || cfg?.targetRoomId || null;

  const meta = {
    object_id: idStr,
    element_type: type,
    object_type: type,
    object_label: label,
    is_vapi: isVapi,
  };
  if (name) meta.object_name = name;
  if (cfg?.id != null && String(cfg.id) !== idStr) meta.db_config_id = String(cfg.id);
  if (parent?.unique_id != null) meta.unique_id = String(parent.unique_id);
  if (mesh?.name) meta.mesh_name = clip(mesh.name);
  if (mesh?.type && mesh.type !== type) meta.mesh_type = String(mesh.type);
  if (cfg?.url) meta.url = clip(cfg.url, 200);
  if (targetSceneId) {
    meta.go_to_scene_id = String(targetSceneId);
    const targetName = resolveSceneName(view, targetSceneId);
    if (targetName) meta.go_to_scene_name = clip(targetName);
  }
  if (cfg?.data?.title) meta.info_title = clip(cfg.data.title);
  if (cfg?.data?.button?.text) meta.info_button = clip(cfg.data.button.text);
  if (isVapi) {
    meta.chair_name = clip(cfg?.chairName || chair?._getAgentDisplayName?.());
    if (cfg?.chairScenario || cfg?.scenarioName) meta.scenario = clip(cfg.chairScenario || cfg.scenarioName);
    if (cfg?.VAPI_ASSISTANT_ID) meta.assistant_id = String(cfg.VAPI_ASSISTANT_ID);
    if (cfg?.VAPI_SQUAD_ID) meta.squad_id = String(cfg.VAPI_SQUAD_ID);
    meta.is_squad = !!cfg?.isSquad;
  }
  return meta;
}

const CHAT_TYPE_LABELS = {
  ROOM: 'world',
  SCENE: 'scene',
  PRIVATE: 'direct',
  TOUR: 'tour',
  CHESS: 'chess',
};

export function chatTypeForAnalytics(type) {
  if (!type) return 'unknown';
  return CHAT_TYPE_LABELS[type] || String(type).toLowerCase();
}

export { CAMERA_MOVE_COOLDOWN_MS, CAMERA_HOVER_DELAY_MS };
